import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import {useForm} from "react-hook-form";
import {useAuth} from "../hooks/useAuth";
import useAppRouter from "../hooks/useAppRouter";
import emailIcon from '../assets/images/email_icon.png';
import passwordIcon from '../assets/images/password_icon.png';
import {PasswordDisplayButton} from "../components/pages/common/PasswordDisplayButton";
import useLoading from "../hooks/useLoading";
import {FormValidationType, makeRegisterOptions} from "../libs/reactHookForm/makeRegisterOptions";
import {InvalidCredentialError} from "../errors/InvalidCredentialError";
import {TooManyAuthRequestError} from "../errors/TooManyAuthRequestError";
import {config} from "../libs/config/config";
import {logError} from "../libs/utils/logError";

export interface LoginFormValues {
  email: string;
  password: string;
}

export const LoginPage: React.FC = () => {
  const [ errorMessage, setErrorMessage ] = useState<string | null>(null);
  const { goto } = useAppRouter();
  const { login } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onSubmit',
  });
  const [ isShowPassword, setIsShowPassword ] = useState<boolean>(false);
  const [ isTyping, setIsTyping ] = useState<boolean>(false);
  const withLoading = useLoading();
  const location = useLocation();

  const handleLogin = async () => {
    withLoading(async () => {
      setErrorMessage(null);
      await handleSubmit(async (data: LoginFormValues) => {
        try {
          await login(data.email, data.password);
        } catch (e) {
          await logError(
            location.pathname,
            'ログインに失敗しました。',
            e
          );

          if ((e instanceof InvalidCredentialError)
            || (e instanceof TooManyAuthRequestError)) {
            setErrorMessage(e.message);
            return;
          }

          goto('internalServerError');
        }
      })();
    });
  };

  const handleInputKeyDown  = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isTyping) {
      await handleLogin();
    }
  };

  return (
    <>
      <form className="min-h-full bg-primary flex flex-col justify-between p-16">
        <div className="mb-1 text-danger-secondary text-xs">
          {errorMessage}
        </div>

        <div>
          { /* Email */ }
          <div className="mb-12">
            <label htmlFor="email">
              <div className="flex items-center">
                <span className="w-3 mr-1">
                  <img src={emailIcon} className="object-contain" alt="icon" />
                </span>
                <input
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register("email",
                    makeRegisterOptions('メールアドレス', [
                      { type: FormValidationType.REQUIRED },
                      { type: FormValidationType.MAX_STRING_LENGTH, value: 86 },
                    ])
                  )}
                  id="email"
                  className="flex-grow bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none p-2"
                  type="text"
                  maxLength={86}
                  autoComplete="email"
                  placeholder="メールアドレス"
                  onCompositionStart={() => setIsTyping(true)}
                  onCompositionEnd={() => setIsTyping(false)}
                  onKeyDown={handleInputKeyDown}
                />
              </div>
              <div className="mt-1 ml-6 text-danger-secondary text-xs">
                {errors.email?.message}
              </div>
            </label>
          </div>

          { /* パスワード */ }
          <div className="mb-4">
            <label htmlFor="password">
              <div className="flex items-center">
                <span className="w-3 mr-1">
                  <img src={passwordIcon} className="object-contain" alt="icon" />
                </span>
                <span className="relative flex-grow">
                  <input
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...register("password",
                      makeRegisterOptions('パスワード', [
                        { type: FormValidationType.REQUIRED }
                      ])
                    )}
                    id="password"
                    className="min-w-full bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none p-2"
                    type={isShowPassword ? "text" : "password"}
                    maxLength={64}
                    autoComplete="current-password"
                    placeholder="パスワード"
                    onCompositionStart={() => setIsTyping(true)}
                    onCompositionEnd={() => setIsTyping(false)}
                    onKeyDown={handleInputKeyDown}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 text-white flex items-center text-sm leading-5">
                    <PasswordDisplayButton
                      {...{
                        isShow: isShowPassword,
                        setIsShow: setIsShowPassword,
                      }}
                    />
                  </div>
                </span>
              </div>
              <div className="mt-1 ml-6 text-danger-secondary text-xs">
                {errors.password?.message}
              </div>
            </label>
          </div>

          <div className="flex justify-center">
            <button
              type="button"
              className="text-sm text-white font-semibold focus:text-topacity-50"
              onClick={() => goto('passwordResetMailSend')}
            >
              パスワードをお忘れの方はこちら &gt;
            </button>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            className="btn-primary text-6xl border-2 border-white rounded"
            type="button"
            onClick={handleLogin}
          >
            LOGIN
          </button>
        </div>
        <div className="absolute bottom-2 right-2 text-white text-semibold text-sm">バージョン：{ config.app.version }</div>
      </form>
    </>
  );
};
